import { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Filter, Loader2, Clock } from "lucide-react";

export default function ReportsPage() {
  const [employees, setEmployees] = useState([]);
  const [obras, setObras] = useState([]);
  const [filters, setFilters] = useState({ user_id: "", obra_id: "", start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/obras")]).then(([u, o]) => {
      setEmployees(u.data);
      setObras(o.data);
    });
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    return p;
  };

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/summary", { params: params() });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  const query = () => {
    const p = params();
    const qs = new URLSearchParams(p).toString();
    return qs ? `?${qs}` : "";
  };

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const ext = kind === "excel" ? "xlsx" : "pdf";
      await downloadFile(`/reports/export/${kind}${query()}`, `relatorio_monumental.${ext}`);
      toast.success(`Relatório ${kind === "excel" ? "Excel" : "PDF"} exportado`);
    } catch (e) {
      toast.error("Falha na exportação");
    } finally {
      setExporting("");
    }
  };

  return (
    <div data-testid="reports-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Relatórios de Horas</h1>
      <p className="text-muted-foreground mb-6">Filtre por funcionário, obra e período e exporte.</p>

      <div className="bg-card border border-border rounded-xl p-5 mb-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="Funcionário" testid="report-user-select" value={filters.user_id} onChange={(v) => setFilters({ ...filters, user_id: v })} options={employees} />
          <Select label="Obra" testid="report-obra-select" value={filters.obra_id} onChange={(v) => setFilters({ ...filters, obra_id: v })} options={obras} />
          <DateInput label="Início" testid="report-start" value={filters.start} onChange={(v) => setFilters({ ...filters, start: v })} />
          <DateInput label="Fim" testid="report-end" value={filters.end} onChange={(v) => setFilters({ ...filters, end: v })} />
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            data-testid="apply-filters-button"
            onClick={run}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors"
          >
            <Filter className="w-4 h-4" /> Aplicar filtros
          </button>
          <button
            data-testid="export-excel-button"
            onClick={() => doExport("excel")}
            disabled={exporting === "excel"}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60"
          >
            {exporting === "excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Exportar Excel
          </button>
          <button
            data-testid="export-pdf-button"
            onClick={() => doExport("pdf")}
            disabled={exporting === "pdf"}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors disabled:opacity-60"
          >
            {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Exportar PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-primary animate-spin" />
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-5">
              <Clock className="w-6 h-6 text-primary mb-2" />
              <p className="font-data text-3xl font-bold" data-testid="report-total-hours">{data.total_hours} h</p>
              <p className="text-xs text-muted-foreground mt-1">Total de horas</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="font-data text-3xl font-bold">{data.count}</p>
              <p className="text-xs text-muted-foreground mt-1">Registos</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="font-data text-3xl font-bold">{data.by_employee.length}</p>
              <p className="text-xs text-muted-foreground mt-1">Funcionários com horas</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display font-semibold">Detalhe dos registos</h2>
            </div>
            {data.rows.length === 0 ? (
              <p className="p-8 text-center text-muted-foreground">Sem registos para os filtros selecionados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b border-border">
                    <tr>
                      <th className="px-5 py-3 font-medium">Funcionário</th>
                      <th className="px-5 py-3 font-medium">Obra</th>
                      <th className="px-5 py-3 font-medium">Tipo</th>
                      <th className="px-5 py-3 font-medium">Entrada</th>
                      <th className="px-5 py-3 font-medium">Saída</th>
                      <th className="px-5 py-3 font-medium text-right">Horas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.id} className="border-b border-border/50 last:border-0">
                        <td className="px-5 py-3 font-medium">{r.user_name}</td>
                        <td className="px-5 py-3 text-muted-foreground">{r.obra_name || "—"}</td>
                        <td className="px-5 py-3 text-muted-foreground">{r.tipo_trabalho || "—"}</td>
                        <td className="px-5 py-3 font-data">{fmt(r.clock_in)}</td>
                        <td className="px-5 py-3 font-data">{r.clock_out ? fmt(r.clock_out) : "em curso"}</td>
                        <td className="px-5 py-3 text-right font-data font-semibold">{r.hours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
      >
        <option value="">— Todos —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function DateInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
      />
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
